import { logException } from "@helpers/logException/logException";
import {
  addHttpErrorBreadcrumb,
  addHttpResponseBreadcrumb,
  getAxiosErrorFingerprint,
  getAxiosErrorTags,
  prepareRequestCorrelation,
} from "@helpers/logException/requestCorrelation";
import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { refreshOnce } from "./refreshOnce";
import store from "@state/store";
import { signOutAsync } from "@state/auth/auth.actions";
import { viewAsSessionEnded } from "@state/viewAs/viewAs.reducer";
import { NODE_ENV } from "gatsby-env-variables";
import { AnyAction } from "@reduxjs/toolkit";
import * as Sentry from "@sentry/browser";
import { toast } from "react-toastify";
import {
  createViewAsEndedError,
  readViewAsRefusalCode,
  VIEW_AS_READ_ONLY_MESSAGE,
  VIEW_AS_READ_ONLY_TOAST_ID,
  VIEW_AS_REFUSAL_CODES,
} from "@helpers/viewAs/viewAsRefusal";
import {
  applyViewAsHeader,
  shouldSendViewAsHeader,
  readRequestViewAsSessionId,
} from "@helpers/viewAs/viewAsRequest";
import { getViewAsSessionId, isViewAsTab } from "@helpers/viewAs/viewAsSession";
import {
  clearViewAsSession,
  readStoredViewAsSessionId,
} from "@helpers/viewAs/viewAsStorage";
const excludedEndpoints = [
  "/auth/refresh-access-token",
  "/auth/2fa/details",
  "/auth/login",
  "/auth/forgot-password/login",
  "/auth/reset-password",
];
const WHITELISTED_PATHS = [
  "/login",
  "/signup",
  "/reset-password",
  "/signup-apple",
  "/verify-email",
  "/forgot-password",
];
const isExcludedEndpoint = (url: string) =>
  excludedEndpoints.some((ex) => {
    const r = new RegExp(ex, "g");
    return url.match(r);
  });

const isViewAsOver = (): boolean => {
  const { ended, ending } = store.store.getState().viewAs;
  return ended || ending;
};

const READ_METHODS = ["get", "head", "options"];

const isWriteRequest = (config?: AxiosRequestConfig): boolean =>
  !READ_METHODS.includes((config?.method ?? "get").toLowerCase());

// Only a refusal of this page's own session ends it; a late refusal must not delete a newer session's id.
const endViewAsSession = (config?: AxiosRequestConfig): void => {
  const requestSessionId = readRequestViewAsSessionId(config);
  // A refusal on a request that named no session is not about this tab.
  if (!requestSessionId || requestSessionId !== getViewAsSessionId()) {
    return;
  }

  if (requestSessionId === readStoredViewAsSessionId()) {
    clearViewAsSession();
  }

  store.store.dispatch(viewAsSessionEnded());
};

export const axiosErrorInterceptor = (): number => {
  return axios.interceptors.response.use(
    (response) => {
      // An answer in flight when the session ended must not reach a tab that is now over; the staff member's own token refresh may.
      if (isViewAsOver() && shouldSendViewAsHeader(response.config)) {
        return Promise.reject(createViewAsEndedError());
      }

      return addHttpResponseBreadcrumb(response);
    },
    async function (e) {
      addHttpErrorBreadcrumb(e);

      if (e.message === "Network Error" && e.response === undefined) {
        return Promise.reject(e);
      }

      if (e?.response?.status >= 500) {
        // Correlation ids are derived from the axios error inside
        // logException; the explicit tags add the endpoint dimension and the
        // fingerprint splits issues per endpoint instead of one catch-all
        // "status 500" issue grouped on the axios constructor frame.
        logException({
          tag: "axiosErrorInterceptor",
          exception: e,
          tags: getAxiosErrorTags(e),
          fingerprint: getAxiosErrorFingerprint(e),
        });
      }

      const originalRequest = e.config;

      if (!e.response) {
        return Promise.reject(e);
      }

      const refusal = readViewAsRefusalCode(e);

      // Invalid, or the feature is off (which refuses the end call too): the session is over.
      if (
        refusal === VIEW_AS_REFUSAL_CODES.INVALID ||
        refusal === VIEW_AS_REFUSAL_CODES.DISABLED
      ) {
        endViewAsSession(originalRequest);
        return Promise.reject(e);
      }

      if (refusal === VIEW_AS_REFUSAL_CODES.UNSUPPORTED) {
        // Only a refused write gets the toast; a refused read is a screen whose empty state says so.
        if (isWriteRequest(originalRequest)) {
          toast.error(VIEW_AS_READ_ONLY_MESSAGE, {
            toastId: VIEW_AS_READ_ONLY_TOAST_ID,
          });
        }
        return Promise.reject(e);
      }

      if (
        e.response?.status === 401 &&
        isExcludedEndpoint(originalRequest.url)
      ) {
        return Promise.reject(e);
      }
      if (e.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        const res = await refreshOnce();

        if (res && res?.status === 201) {
          return axios(originalRequest);
        }

        if (isViewAsTab()) {
          // Another tab may have refreshed first, so the cookies can be current although this refresh lost the race.
          try {
            return await axios(originalRequest);
          } catch (retryError) {
            if ((retryError as AxiosError).response?.status === 401) {
              endViewAsSession(originalRequest);
            }
            return Promise.reject(retryError);
          }
        }

        const currentPath = window.location.pathname;
        const isWhitelisted = WHITELISTED_PATHS.some((path) =>
          currentPath.includes(path)
        );

        if (!isWhitelisted) {
          store.store.dispatch(
            signOutAsync({ returnToCurrentPage: true }) as unknown as AnyAction
          );
        }
        return Promise.reject(e);
      }

      // When running the integration test
      // the address search will return 401 so often the interceptor will log the user out
      // making the tests failing
      const isTheGoogleSearch = originalRequest.url.match(/query\?place/gi);
      const isAValidationIssue =
        e.response?.status >= 400 || e.response?.status < 500;

      if (
        !isAValidationIssue &&
        (NODE_ENV !== "development" || !isTheGoogleSearch)
      ) {
        store.store.dispatch(signOutAsync() as unknown as AnyAction);
      }

      return Promise.reject(e);
    }
  );
};

// Registered before the app renders: the first request carries the session and none leaves once it has ended.
export const viewAsRequestInterceptor = (): number => {
  return axios.interceptors.request.use((config) => {
    // Only requests that would carry the session stop; the token refresh authenticates the staff member and Exit's retry needs it.
    if (isViewAsOver() && shouldSendViewAsHeader(config)) {
      return Promise.reject(createViewAsEndedError());
    }

    return applyViewAsHeader(config);
  });
};

export const axiosRequestInterceptor = (): number => {
  return axios.interceptors.request.use((config) => {
    const activeSpan = Sentry.getActiveSpan();
    const rootSpan = activeSpan ? Sentry.getRootSpan(activeSpan) : undefined;

    const sentryTraceHeader = rootSpan
      ? Sentry.spanToTraceHeader(rootSpan)
      : undefined;

    const sentryBaggageHeader = rootSpan
      ? Sentry.spanToBaggageHeader(rootSpan)
      : undefined;

    config.headers["sentry-trace"] = sentryTraceHeader;
    config.headers["baggage"] = sentryBaggageHeader;

    return prepareRequestCorrelation(config);
  });
};
